import type { AriaAttributes, CSSProperties, FC } from 'react';

import { cva } from 'cva';

import { pickAriaProps } from '../../utils/pick_aria_props';
import { type IconName, icons } from './generated_icons';

import styles from './icon.module.css';

export const iconVariants = cva({
  base: styles.icon,
  variants: {
    size: {
      '4xs': styles.size_4xs,
      '3xs': styles.size_3xs,
      '2xs': styles.size_2xs,
      xs: styles.size_xs,
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
      xl: styles.size_xl,
    },
    color: {
      primary_green: styles.color_primary_green,
      primary_pink: styles.color_primary_pink,
      primary_teal: styles.color_primary_teal,
      primary_yellow: styles.color_primary_yellow,
      primary_purple: styles.color_primary_purple,
      primary_label: styles.color_primary_label,
      primary_orange: styles.color_primary_orange,
      primary_blue: styles.color_primary_blue,
      primary_red: styles.color_primary_red,
      primary_inverted: styles.color_primary_inverted,
      primary_turquoise: styles.color_primary_turquoise,
      primary_indigo: styles.color_primary_indigo,
      primary_lilac: styles.color_primary_lilac,
      primary_neutral: styles.color_primary_neutral,
      secondary_label: styles.color_secondary_label,
      secondary_pink: styles.color_secondary_pink,
      secondary_red: styles.color_secondary_red,
      secondary_orange: styles.color_secondary_orange,
      secondary_yellow: styles.color_secondary_yellow,
      secondary_green: styles.color_secondary_green,
      secondary_turquoise: styles.color_secondary_turquoise,
      secondary_teal: styles.color_secondary_teal,
      secondary_blue: styles.color_secondary_blue,
      secondary_indigo: styles.color_secondary_indigo,
      secondary_purple: styles.color_secondary_purple,
      secondary_lilac: styles.color_secondary_lilac,
      secondary_neutral: styles.color_secondary_neutral,
      secondary_inverted: styles.color_secondary_inverted,
    },
  },
  defaultVariants: {
    size: 's',
  },
});

export type IconColor =
  | 'primary_green'
  | 'primary_pink'
  | 'primary_teal'
  | 'primary_yellow'
  | 'primary_purple'
  | 'primary_label'
  | 'primary_orange'
  | 'primary_blue'
  | 'primary_red'
  | 'primary_inverted'
  | 'primary_turquoise'
  | 'primary_indigo'
  | 'primary_lilac'
  | 'primary_neutral'
  | 'secondary_label'
  | 'secondary_pink'
  | 'secondary_red'
  | 'secondary_orange'
  | 'secondary_yellow'
  | 'secondary_green'
  | 'secondary_turquoise'
  | 'secondary_teal'
  | 'secondary_blue'
  | 'secondary_indigo'
  | 'secondary_purple'
  | 'secondary_lilac'
  | 'secondary_neutral'
  | 'secondary_inverted';

export type IconSize = '4xs' | '3xs' | '2xs' | 'xs' | 's' | 'm' | 'l' | 'xl';

export type IconProps = AriaAttributes & {
  name: IconName;
  size?: IconSize;
  className?: string;
  color?: IconColor;
  style?: CSSProperties;
};

// TODO migrate to sprite-based icons eventually (or to another efficient way)
export const Icon: FC<IconProps> = ({
  name,
  size,
  color,
  className,
  style,
  ...props
}) => {
  const classes = iconVariants({
    size,
    color,
    className,
  });

  const IconComponent = icons[name];
  if (!IconComponent) {
    return null;
  }
  return (
    <IconComponent
      {...pickAriaProps(props)}
      className={classes}
      style={style}
      data-icon={name}
    />
  );
};
